/**
 * PulseSparkline — the 48h trend graph shared across detail screens.
 *
 * Uses Recharts (already in package.json). Draws:
 *  • A smooth area chart with the metric color
 *  • An optional ideal-range band (greenish dashed strip)
 *  • A "Now" tooltip pinned at the latest value
 *
 * Empty state: shows a friendly "Waiting for sensor history…" message so
 * a new user with <2 readings doesn't see a broken graph.
 */
import React, { useEffect, useState } from 'react';
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis,
         ReferenceArea, Tooltip, CartesianGrid } from 'recharts';
import { API_URL } from '../../config/api';

const COLORS = {
  moisture: { stroke: '#38bdf8', fill: '#0ea5e9' }, // sky
  ec:       { stroke: '#34d399', fill: '#10b981' }, // emerald
  vpd:      { stroke: '#a78bfa', fill: '#8b5cf6' }, // violet
  dewpoint: { stroke: '#f59e0b', fill: '#d97706' }, // amber
  temp:     { stroke: '#fb7185', fill: '#e11d48' }, // rose
  soil_temp:{ stroke: '#fb923c', fill: '#ea580c' }, // orange
};

function fmtTime(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const h = d.getHours();
    return `-${Math.max(0, Math.round((Date.now() - d.getTime()) / 3.6e6))}h`;
  } catch { return ''; }
}

export function PulseSparkline({
  metric = 'moisture',
  hours = 48,
  idealMin = null,
  idealMax = null,
  unit = '',
  height = 200,
}) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let live = true;
    fetch(`${API_URL}/api/dashboard/history?metric=${metric}&hours=${hours}`, {
      credentials: 'include',
    })
      .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
      .then(j => { if (live) setData(j); })
      .catch(e => { if (live) setErr(e.message); });
    return () => { live = false; };
  }, [metric, hours]);

  const color = COLORS[metric] || COLORS.moisture;

  if (err) return (
    <div className="bg-slate-900/80 border border-rose-900/60 rounded-2xl p-4 text-xs text-rose-300">
      Failed to load history: {err}
    </div>
  );
  if (!data) return (
    <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 text-xs text-slate-400 animate-pulse"
         style={{ minHeight: height }}>
      Loading 48h trend…
    </div>
  );
  if (!data.series || data.series.length < 2) return (
    <div className="bg-slate-900/80 border border-slate-800 rounded-2xl p-4 text-center text-xs text-slate-400"
         style={{ minHeight: height }}>
      <div className="pt-12">Waiting for sensor history (need ≥2 readings)…</div>
    </div>
  );

  const chartData = data.series.map(s => ({
    t: fmtTime(s.t),
    v: s.v,
    raw: s.t,
  }));
  const lastVal = data.last;
  const values = chartData.map(p => p.v);
  const minV = Math.min(...values);
  const maxV = Math.max(...values);
  const padding = Math.max((maxV - minV) * 0.15, 0.5);
  const yMin = Math.floor(minV - padding);
  const yMax = Math.ceil(maxV + padding);

  return (
    <div data-testid={`pulse-sparkline-${metric}`}
         className="bg-slate-900/80 border border-slate-800 rounded-2xl p-3" style={{ minHeight: height }}>
      <ResponsiveContainer width="100%" height={height - 30}>
        <AreaChart data={chartData} margin={{ top: 16, right: 12, bottom: 0, left: -10 }}>
          <defs>
            <linearGradient id={`g-${metric}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color.fill} stopOpacity={0.55} />
              <stop offset="100%" stopColor={color.fill} stopOpacity={0.02} />
            </linearGradient>
          </defs>
          <CartesianGrid stroke="#1e293b" strokeDasharray="2 4" vertical={false} />
          <XAxis dataKey="t" tick={{ fill: '#64748b', fontSize: 10 }}
                 tickLine={false} axisLine={false} interval="preserveStartEnd" />
          <YAxis domain={[yMin, yMax]} tick={{ fill: '#64748b', fontSize: 10 }}
                 tickLine={false} axisLine={false} width={32} />
          {idealMin !== null && idealMax !== null && (
            <ReferenceArea y1={idealMin} y2={idealMax}
                           fill="#10b981" fillOpacity={0.07}
                           stroke="#10b981" strokeOpacity={0.4} strokeDasharray="3 3" />
          )}
          <Tooltip
            contentStyle={{ background: '#0f172a', border: '1px solid #334155',
                            borderRadius: 8, fontSize: 12 }}
            labelStyle={{ color: '#94a3b8' }}
            formatter={(v) => [`${v}${unit}`, 'Value']}
          />
          <Area type="monotone" dataKey="v" stroke={color.stroke} strokeWidth={2.5}
                fill={`url(#g-${metric})`} dot={false}
                activeDot={{ r: 4, fill: color.stroke, stroke: '#0f172a', strokeWidth: 2 }} />
        </AreaChart>
      </ResponsiveContainer>
      <div className="flex items-center justify-between mt-1 text-[10px] text-slate-500">
        <span>{hours}h</span>
        <span><b className="text-slate-300">Now</b> {lastVal}{unit}</span>
      </div>
    </div>
  );
}
